import axios from 'axios';
import { TOKEN } from '@/constants/labels';
import { APIS_TELEMETRY, APIS_INVENTORY } from "@/constants/apis";

const getHeaders = () => ({
    'x-api-key': TOKEN,
    'Authorization': `Bearer ${TOKEN}`
});

export interface TelemetryPoint {
    id: number;
    ip: string;
    fecha: string;
    latitud: number;
    longitud: number;
    altitud: number;
    name: string;
    tipo?: string;
    subtipo?: string;
}

export interface TelemetryResponse {
    status: number;
    count: number;
    date: string;
    turn: string;
    range: { start: string; end: string };
    data: TelemetryPoint[];
}

/**
 * Obtener historial de ubicaciones GPS para el Playback (Acepta IP individual o Arreglo de IPs)
 */
export async function getTelemetryHistory(date: string, turn?: string, ip?: string | string[]): Promise<TelemetryResponse | null> {
    try {
        const params: Record<string, string> = { date };
        if (turn && turn !== 'ALL') params.turn = turn;

        if (ip) {
            if (Array.isArray(ip) && ip.length > 0) {
                params.ip = ip.join(',');
            } else if (typeof ip === 'string' && ip !== 'ALL' && ip.trim() !== '') {
                params.ip = ip;
            }
        }

        const response = await axios.get(`${APIS_TELEMETRY.value.history}`, {
            params,
            headers: getHeaders()
        });

        return response.data;
    } catch (error) {
        console.error('Error al obtener historial de telemetría:', error);
        return null;
    }
}

/**
 * Descargar archivo CSV de telemetría por turno
 */
export async function downloadTelemetryCSV(date: string, turn?: string): Promise<void> {
    try {
        const params: Record<string, string> = { date };
        if (turn && turn !== 'ALL') params.turn = turn;

        const response = await axios.get(`${APIS_TELEMETRY.value.csv}`, {
            params,
            headers: getHeaders(),
            responseType: 'blob'
        });

        const turnCode = (turn || 'FULL').toUpperCase();
        const cleanDate = date.replace(/-/g, '');
        const filename = `SL${cleanDate}_${turnCode}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error al descargar el reporte CSV:', error);
    }
}

/**
 * Obtener la lista completa de inventario para el selector de equipos
 */
export async function getInventoryDevices(): Promise<{ ip: string; name: string }[]> {
    try {
        const response = await axios.get(`${APIS_INVENTORY.value.getAllInventoryData}`, {
            headers: getHeaders()
        });
        const rawData = response.data || [];
        const allowedKeywords = ['camion', 'haultruck', 'truck'];

        return rawData
            .filter((item: any) => {
                const tipoNorm = (item.tipo || '')
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/\s+/g, '');

                const subtipoNorm = (item.rol || item.subtipo || '')
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/\s+/g, '');

                return allowedKeywords.some(kw => tipoNorm.includes(kw) || subtipoNorm.includes(kw));
            })
            .map((item: any) => ({
                ip: item.ip,
                name: item.tag || item.name || item.ip
            }));
    } catch (error) {
        console.error('Error al cargar inventario de equipos:', error);
        return [];
    }
}

/**
 * Obtener la lista de equipos de acceso (solartrailers, etc.) para el selector
 */
export async function getAccesoDevices(): Promise<{ ip: string; name: string }[]> {
    try {
        const response = await axios.get(`${APIS_INVENTORY.value.getAllInventoryData}`, {
            headers: getHeaders()
        });
        const rawData = response.data || [];

        return rawData
            .filter((item: any) => {
                const subtipoNorm = (item.rol || item.subtipo || '')
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/\s+/g, '');

                return subtipoNorm === 'acceso';
            })
            .map((item: any) => ({
                ip: item.ip,
                name: item.tag || item.name || item.ip
            }));
    } catch (error) {
        console.error('Error al cargar inventario de equipos de acceso:', error);
        return [];
    }
}